import React, { useCallback, useMemo } from 'react';
import { ProjectDetailsForm } from '@/components/Forms/ProjectDetailsForm';
import { RangeForm } from '@/components/TargetCost/Forms/RangeForm';
import { CostsForm } from '@/components/Forms/CostsForm';
import { MetricsGrid } from '@/components/TargetCost/Metrics/MetricsGrid';
import { CostComparisonChart } from '@/components/TargetCost/Charts/CostComparisonChart';
import { ShareDistributionChart } from '@/components/TargetCost/Charts/ShareDistributionChart';
import { Container } from '@/components/Layout/Container';
import { PrimaryHeader } from '@/components/Layout/Header/PrimaryHeader';
import { SecondaryHeader } from '@/components/Layout/Header/SecondaryHeader';
import { useTargetCost } from '@/context/TargetCostContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TargetCostData } from '@/types/target-cost';

export function TargetCostTool() {
  const { state, dispatch } = useTargetCost();
  const { data } = state;

  /**
   * A strongly typed update handler.
   * This function is used to update various sections of the global data
   * (e.g. "details", "costs", "ranges"). Notably, the CostsForm component
   * automatically calculates *Savings* and *Cost Overrun* based on user input,
   * and then calls this handler to update the global state.
   *
   * @param field - The key of the section being updated.
   * @param updates - The new values to merge into that section.
   */
  const handleUpdate = useCallback(
    <K extends keyof TargetCostData>(
      field: K,
      updates: Partial<TargetCostData[K]>
    ) => {
      dispatch({
        type: 'UPDATE_DATA',
        payload: {
          ...data,
          [field]: {
            ...data[field],
            ...updates,
          },
        },
      });
    },
    [data, dispatch]
  );

  // Memoize derived values for the RangeForm.
  const targetPrice = useMemo(() => data.costs?.totalPrices ?? 0, [data.costs]);
  const actualCostPct = useMemo(() => {
    if (data.costs?.finalPWDD != null) {
      return (data.costs.finalPWDD / (targetPrice || 1)) * 100;
    }
    return 0;
  }, [data.costs, targetPrice]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PrimaryHeader />
      <SecondaryHeader />

      <main className="flex-1">
        <Container>
          <div className="space-y-6 py-8">
            {/* Project Details Section */}
            <Card>
              <CardHeader>
                <CardTitle>Project Details</CardTitle>
              </CardHeader>
              <CardContent>
                <ProjectDetailsForm
                  details={data.details}
                  onChange={(updates) => handleUpdate('details', updates)}
                />
              </CardContent>
            </Card>

            {/* Target and Actual Costs Section */}
            {/* 
              NOTE: The CostsForm component is responsible for capturing the target and actual costs.
              It automatically calculates *Savings* and *Cost Overrun* in real time, and calls onChange
              to update the global state with these derived values.
            */}
            <Card>
              <CardHeader>
                <CardTitle>Target and Actual Costs</CardTitle>
              </CardHeader>
              <CardContent>
                <CostsForm
                  costs={data.costs}
                  onChange={(updates) => handleUpdate('costs', updates)}
                />
              </CardContent>
            </Card>

            {/* Pain/Gain Share Ranges */}
            <RangeForm
              ranges={data.ranges}
              targetPrice={targetPrice}
              actualCostPct={actualCostPct}
              onChange={(updates) => handleUpdate('ranges', updates)}
            />

            {/* Metrics Grid */}
            <MetricsGrid data={data} />

            {/* Cost Performance Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CostComparisonChart data={data} />
              <ShareDistributionChart data={data} />
            </div>
          </div>
        </Container>
      </main>
    </div>
  );
}
